import React from 'react';
import { Linking, Modal, Pressable, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Avatar from '../ui/Avatar';
import Icon from '../ui/Icon';
import Text from '../ui/Text';
import { colors, radii, screenPadding, spacing, useResponsive } from '../../theme';
import { useVoiceCall } from '../../voice/VoiceCallProvider';
import { nameOf } from '../../voice/types';

/**
 * Somebody is calling. The one screen that interrupts.
 *
 * ---------------------------------------------------------------------------
 * WHY A MODAL AND NOT A SCREEN
 * ---------------------------------------------------------------------------
 * This app has no navigator - `HomeScreen` returns one of two dozen screens
 * from a chain of early guards, and there is no stack to push onto. A React
 * Native `Modal` floats over the entire tree from wherever it is mounted, so
 * mounting this once in `App.tsx` makes a call interrupt the catalogue, a
 * booking, a sheet and the cart identically, with no branch added to that
 * chain and nothing changed in its back-button `unwind`.
 *
 * ---------------------------------------------------------------------------
 * WHY BACK DOES NOTHING
 * ---------------------------------------------------------------------------
 * `onRequestClose` is required on Android and is deliberately inert here. Back
 * is the reflex press, and a ringing call has exactly two honest answers:
 * accept, or decline. Dismissing the card without answering would leave the
 * call ringing on the other person's phone with nothing on this one to show
 * for it - and if nobody touches either button the server's own ring timeout
 * closes it as missed, which is the truthful outcome.
 */

/** Diameter of the two answer controls. Thumb-sized, and equal. */
const ACTION = 68;

function IncomingCallOverlay() {
  const voice = useVoiceCall();
  const insets = useSafeAreaInsets();
  const resp = useResponsive();

  const visible = voice.phase === 'incoming';
  const who = nameOf(voice.call);

  return (
    <Modal
      visible={visible}
      animationType="fade"
      /* Opaque rather than transparent: this is not a sheet over the app, it
         is the only thing that matters while it is up. */
      transparent={false}
      statusBarTranslucent
      onRequestClose={() => {
        /* See the note above. Intentionally empty. */
      }}
    >
      <View
        style={[
          styles.root,
          {
            paddingTop: insets.top + spacing.xxxl,
            paddingBottom: insets.bottom + spacing.xxxl,
            paddingHorizontal: screenPadding + resp.sideInset,
          },
        ]}
      >
        <View style={styles.identity}>
          <Text variant="eyebrow" color={colors.inkInverseMuted}>
            Incoming call
          </Text>

          <Avatar name={who} size={112} tone="inverse" style={styles.avatar} />

          <Text variant="titleLg" color={colors.inkInverse} align="center">
            {who}
          </Text>

          {/* What they are calling about, so the name is not the only context.
              Never a phone number - there is not one anywhere in this product. */}
          <Text variant="bodySm" color={colors.inkInverseMuted} align="center">
            {voice.call?.subjectType === 'ride'
              ? 'About your ride'
              : 'About your booking'}
          </Text>
        </View>

        {/*
          The reason answering did not work, on the screen that was pressed.

          A refused microphone lands here rather than anywhere else: the call
          is still ringing, the customer can still grant it and press Accept
          again, and this is the only surface they are looking at.
        */}
        {voice.notice ? (
          <View style={styles.notice}>
            <Icon name="alert" size={16} color={colors.inkInverse} />
            <View style={styles.noticeBody}>
              <Text
                variant="caption"
                color={colors.inkInverse}
                style={styles.noticeText}
              >
                {voice.notice}
              </Text>

              {/*
                The way out of a permission nothing will ask for again.

                Without this the notice was a dead end: the sentence named
                Settings, and the only two controls on the screen were Accept -
                which fails the same way every press - and Decline, which hangs
                up on the person calling. `openSettings` is the same call
                `LocationScreen` makes for a refused location.
              */}
              {voice.micBlocked ? (
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="Open app settings to turn on the microphone"
                  onPress={() => {
                    Linking.openSettings().catch(() => undefined);
                  }}
                  hitSlop={8}
                >
                  <Text variant="labelSm" color={colors.inkInverse} style={styles.noticeAction}>
                    Open Settings
                  </Text>
                </Pressable>
              ) : null}
            </View>
          </View>
        ) : null}

        <View style={styles.actions}>
          <View style={styles.action}>
            {/*
              A solid red circle, which this app otherwise reserves - the house
              `danger` button is a pale fill on purpose. A call screen is the
              one exception worth making: red-to-decline and green-to-answer is
              how every dialler on both platforms draws it, and the two have to
              carry equal visual weight so neither reads as the default choice.
            */}
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={`Decline call from ${who}`}
              onPress={voice.reject}
              style={({ pressed }) => [
                styles.circle,
                styles.decline,
                pressed ? styles.pressed : null,
              ]}
            >
              <Icon name="phoneEnd" size={28} color={colors.onDanger} />
            </Pressable>
            <Text variant="labelSm" color={colors.inkInverseMuted}>
              Decline
            </Text>
          </View>

          <View style={styles.action}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={`Accept call from ${who}`}
              onPress={voice.accept}
              style={({ pressed }) => [
                styles.circle,
                styles.accept,
                pressed ? styles.pressed : null,
              ]}
            >
              <Icon name="phoneIncoming" size={28} color={colors.onPrimary} />
            </Pressable>
            <Text variant="labelSm" color={colors.inkInverseMuted}>
              Accept
            </Text>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    justifyContent: 'space-between',
    backgroundColor: colors.surfaceInverse,
  },
  identity: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
  },
  avatar: {
    marginBottom: spacing.lg,
  },
  notice: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    padding: spacing.md,
    marginBottom: spacing.xl,
    borderRadius: radii.lg,
    backgroundColor: colors.overlay,
  },
  noticeBody: {
    flex: 1,
    gap: spacing.sm,
  },
  /* Underlined because it sits on the same tinted panel as the sentence above
     it, where colour alone is not enough to read as a control. */
  noticeAction: {
    textDecorationLine: 'underline',
  },
  /* No `flex: 1` - `noticeBody` above carries it now. Leaving it here made
     the sentence and the Settings link fight for the same column height. */
  noticeText: {},
  actions: {
    flexDirection: 'row',
    justifyContent: 'space-evenly',
  },
  action: {
    alignItems: 'center',
    gap: spacing.sm,
  },
  circle: {
    width: ACTION,
    height: ACTION,
    borderRadius: ACTION / 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  decline: {
    backgroundColor: colors.danger,
  },
  /* `success` rather than `primary`: the brand green is nearly the colour of
     the ground this sits on, and an answer button you have to hunt for is not
     an answer button. */
  accept: {
    backgroundColor: colors.success,
  },
  pressed: {
    opacity: 0.6,
  },
});

export default IncomingCallOverlay;
